import { Avatar, Icon, Menu, MenuItem } from "./";
import { MenuOutlined } from "@ant-design/icons";
import { Drawer, Switch } from "antd";
import { useState } from "react";
import { useRouter } from "next/router";
import { Dropdown } from "antd";
import { Question } from "react-bootstrap-icons";
import Link from "next/link";
import styles from "./navigation.module.scss";

export type Toggle = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  style?: { backgroundColor?: string };
};

export type HeaderProps = {
  menuItems?: MenuItem[];
  title?: string;
  toggles?: Toggle[];
  navItems?: MenuItem[];
  helpItem?: MenuItem;
  logo?: string;
  activeUser?: any;
  userItems?: MenuItem[];
};

const Header = ({
  menuItems,
  title = "Menu",
  toggles = [],
  navItems = [],
  helpItem,
  logo,
  activeUser,
  userItems = [],
}: HeaderProps) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const router = useRouter();
  const pathBeforeQuery = router.asPath.split("?")[0];
  const activePage = pathBeforeQuery.split("/").pop();

  const headerNavItems = [...navItems].reverse();

  const menu = <Menu className="bg-white br-1 s-1" items={userItems} />;

  return (
    <>
      <nav
        className={`flex justify-between pa-3 items-center`}
        style={{
          minHeight: 80,
          height: "7vh",
          borderBottom: "1px solid #ededed",
        }}
      >
        <div className="flex align-items">
          {/* Mobile Menu Button (hidden on desktop or if there is no user) */}
          {activeUser && menuItems?.length && (
            <button
              className="di md_dn bg-transparent bn pointer"
              onClick={() => setShowMobileMenu(true)}
            >
              <MenuOutlined className="f-5" />
            </button>
          )}
          {logo && (
            <img
              src={logo}
              height="40px"
              className="inline self-center ml-3"
              alt="Organization Logo"
            />
          )}
        </div>

        {activeUser && (
          <div className="flex align-items">
            {/* Navigation Items */}
            <div className="dn md_flex">
              <div
                className={"flex items-center bg-transparent overflow-hidden"}
              >
                {headerNavItems.map((nav) => {
                  const linkContent = (
                    <div
                      className="flex items-center justify-center bg-gray-2 lightenPrimaryBkd"
                      style={{
                        minWidth: "120px",
                        height: "44px",
                        borderRadius: "12% / 50%",
                        padding: "0 20px",
                        marginRight: "15px",
                        textAlign: "center",
                        transition: "0.25s",
                      }}
                    >
                      {nav.icon && (
                        <Icon name={nav.icon} className="primary mr-2" />
                      )}
                      <span
                        style={{
                          color: "#010101",
                          textDecoration: "none",
                          lineHeight: "1.2",
                          fontSize: "16px",
                        }}
                      >
                        {nav.name}
                      </span>
                      {nav.submenu?.length ? (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            color: "#010101",
                          }}
                        >
                          ▾
                        </span>
                      ) : null}
                    </div>
                  );

                  // A nav item with a submenu renders as a dropdown (e.g.
                  // "Agentic AI" → AI Growth Agents / Audience Builder),
                  // matching acquire-portal. Plain items stay flat links.
                  if (nav.submenu?.length) {
                    return (
                      <Dropdown
                        key={nav.id}
                        overlay={
                          <Menu
                            className="bg-white br-1 s-1"
                            items={nav.submenu}
                          />
                        }
                        trigger={["click"]}
                        placement="bottomLeft"
                      >
                        <div className="pointer">{linkContent}</div>
                      </Dropdown>
                    );
                  }

                  return nav.externalUrl ? (
                    <a
                      key={nav.id}
                      href={nav.externalUrl}
                      rel="noopener noreferrer"
                    >
                      {linkContent}
                    </a>
                  ) : (
                    <Link key={nav.id} href={nav.path || "#"}>
                      {linkContent}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Toggles (like sandbox mode) */}
            {toggles.map((toggle, index) => (
              <div
                key={index}
                className="flex justify-between items-center ph-6"
              >
                <p className="mb-0 mr-2">{toggle.label} </p>
                <Switch
                  size="small"
                  checked={toggle.checked}
                  onChange={toggle.onChange}
                  style={toggle.style}
                />
              </div>
            ))}

            {/* Help Button */}
            {helpItem && (
              <>
                {helpItem.externalUrl ? (
                  <a href={helpItem.externalUrl} rel="noopener noreferrer">
                    <div
                      className={`${styles.navHelpButton} b-primary primary lightenPrimaryBkd`}
                      title={helpItem.name || "Help"}
                    >
                      <Question />
                    </div>
                  </a>
                ) : (
                  <Link href={helpItem.path || "#"}>
                    <div
                      className={`${styles.navHelpButton} b-primary primary lightenPrimaryBkd`}
                      title={helpItem.name || "Help"}
                    >
                      <Question />
                    </div>
                  </Link>
                )}
              </>
            )}

            <Dropdown overlay={menu} trigger={["click"]} placement="bottomLeft">
              <div className="pointer" onClick={(e) => e.preventDefault}>
                <Avatar
                  firstName={activeUser.attributes.firstName}
                  lastName={activeUser.attributes.lastName}
                  size={52}
                  className="ml-2"
                />
              </div>
            </Dropdown>
          </div>
        )}

        {/* MOBILE VIEW - When menu icon is clicked, Full-Screen "Drawer" slides down from top filled with a menu using 
          the same navigation content as above and a menu of the page-specific items passed in. */}
        <Drawer
          visible={showMobileMenu}
          footer={null}
          onClose={() => setShowMobileMenu(false)}
          placement="top"
          height="100%"
        >
          {/* Show navigation bar and user avatar if there is a user logged in */}
          {activeUser && (
            <div className={`flex flex align-items pa-2`}>
              <div className="flex align-items justify-center">
                {/* Navigation Items */}
                <div className="dn md_flex">
                  <div
                    className={
                      "flex items-center bg-transparent overflow-hidden"
                    }
                  >
                    {headerNavItems.map((nav) => {
                      const linkContent = (
                        <div
                          className="flex items-center justify-center bg-gray-2 lightenPrimaryBkd"
                          style={{
                            minWidth: "120px",
                            height: "44px",
                            borderRadius: "12% / 50%",
                            padding: "0 20px",
                            marginRight: "15px",
                            textAlign: "center",
                            transition: "0.25s",
                          }}
                        >
                          {nav.icon && (
                            <Icon name={nav.icon} className="primary mr-2" />
                          )}
                          <span
                            style={{
                              color: "#010101",
                              textDecoration: "none",
                              lineHeight: "1.2",
                              fontSize: "16px",
                            }}
                          >
                            {nav.name}
                          </span>
                        </div>
                      );

                      return nav.externalUrl ? (
                        <a
                          key={nav.id}
                          href={nav.externalUrl}
                          rel="noopener noreferrer"
                        >
                          {linkContent}
                        </a>
                      ) : (
                        <Link key={nav.id} href={nav.path || "#"}>
                          {linkContent}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div>
            {/* Navigation Items */}
            <Menu
              className="h-100 bg-white mb-4"
              items={navItems}
              onSelect={() => setShowMobileMenu(false)}
            />
            {/* Page-specific Menu Items */}
            <Menu
              className="h-100 bg-white"
              title={title}
              titleClassName="gray-7 f-4 ph-3"
              items={menuItems}
              activeItem={activePage}
              onSelect={() => setShowMobileMenu(false)}
            />
          </div>
        </Drawer>
      </nav>
    </>
  );
};

export default Header;
